import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/cn';

const inputCls = (className?: string) =>
  cn(
    'h-9 w-full rounded-lg border border-border bg-surface px-3 text-sm text-fg',
    'placeholder:text-fg-subtle',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
    'aria-[invalid=true]:border-danger',
    className,
  );

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, type, ...props }, ref) => {
    const [revealed, setRevealed] = useState(false);

    if (type !== 'password') {
      return <input ref={ref} type={type} className={inputCls(className)} {...props} />;
    }

    return (
      <span className="relative block w-full">
        <input
          ref={ref}
          type={revealed ? 'text' : 'password'}
          className={inputCls(cn('pr-9', className))}
          {...props}
        />
        <button
          type="button"
          aria-label={revealed ? 'Hide value' : 'Show value'}
          tabIndex={-1}
          onClick={() => setRevealed((v) => !v)}
          className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-fg-subtle hover:text-fg"
        >
          {revealed ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
        </button>
      </span>
    );
  },
);
Input.displayName = 'Input';

export function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: string;
  htmlFor?: string;
  /** One-line explanation shown under the input — for jargon-dense fields. */
  hint?: string;
  /** Field-level validation message; also flags the field via aria-invalid styling. */
  error?: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <label htmlFor={htmlFor} className="block space-y-1.5" {...(error ? { 'aria-invalid': true } : {})}>
      <span className="text-sm font-medium text-fg">{label}</span>
      {children}
      {error ? (
        <span id={`${id}-error`} role="alert" className="block text-xs text-danger">
          {error}
        </span>
      ) : (
        hint && <span className="block text-xs text-fg-subtle">{hint}</span>
      )}
    </label>
  );
}
